<script setup lang="ts">
import { computed, onMounted, ref, Ref, UnwrapRef, watch } from 'vue';
import { trackingSurveyEvent } from '@/api/tracking';
import {
  FIRST_PUBLISHED_AT,
  searchPresetActivity,
  SURVEY_SEARCH_DEBOUNCE_WAIT_MS,
} from '@/constants';
import DropdownDateRangeCalendar from '@/components/common/dropdown-date-range-calendar.vue';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import TextTabItem from '@/components/common/molecules/text-tab-item.vue';
import SwrScrollingPagination from '@/components/common/swr-scrolling-pagination.vue';
import SurveyCard from '@/components/survey/survey-card.vue';
import { SearchDate, SurveySearchResponse } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { getToday } from '@/utils/dates';
import { debounce } from '@/utils/debounce';
import { formatDate } from '@/utils/formatters';
import {
  SURVEY_SEARCH_PAGE_LIMIT,
  SURVEY_SEARCH_PAGE_NAME,
} from '@/utils/survey/constants';
import { trackSurveyPageView } from '@/utils/survey/tracking';
import { useSurveys, useUserInfo } from '@/utils/swr';
import { formatDateForTracking } from '@/utils/trackingHelper';

const { getCurrentPathForTracking } = useCurrentPathForTracking();

const searchResultTotalCount = ref(0);

const userInfo = useUserInfo();

// 検索条件
const searchKeyword = ref(''); // 検索キーワード
const searchKeywordForApi = ref<string>('');
const searchStartDate = ref<SearchDate | undefined>(); // 開始日
const searchEndDate = ref<SearchDate | undefined>(); // 終了日

const searchConditionKey = computed(() => {
  return [
    searchKeywordForApi.value,
    searchStartDate.value,
    searchEndDate.value,
  ].join('-');
});

watch(searchKeyword, newKeyword => {
  debouncedUpdateKeyword(newKeyword);
});

// 計測用
const searchPeriod = ref<string>('all'); // 検索期間のプリセット

const debouncedUpdateKeyword = debounce((keyword: string) => {
  searchKeywordForApi.value = keyword;
}, SURVEY_SEARCH_DEBOUNCE_WAIT_MS);

const applyDate = (option: {
  presetKey: string;
  value: { from: SearchDate; to: SearchDate };
}) => {
  searchStartDate.value = option.value.from;
  searchEndDate.value = option.value.to;
  searchPeriod.value = option.presetKey;
};

const fetchSurvey = (data: SurveySearchResponse, page: number) => {
  searchResultTotalCount.value = data.total_count;

  const offset = (page - 1) * SURVEY_SEARCH_PAGE_LIMIT;
  trackSurveySearch(offset, SURVEY_SEARCH_PAGE_LIMIT);
};

const paginationFunc = (pageRef: Ref<number>, pageLimit: number) =>
  useSurveys(
    searchKeywordForApi,
    searchStartDate,
    searchEndDate,
    pageRef,
    pageLimit,
  );

const dataAccessor = (
  data: UnwrapRef<ReturnType<typeof useSurveys>['data']>,
) => {
  return data?.surveys;
};

const trackSurveySearch = (offset: number, limit: number) => {
  trackingSurveyEvent('surveys_view', {
    page: {
      name: SURVEY_SEARCH_PAGE_NAME,
      url: getCurrentPathForTracking(),
    },
    request: {
      offset,
      limit,
      period: searchPeriod.value,
      period_detail:
        searchPeriod.value === 'custom' &&
        searchStartDate.value &&
        searchEndDate.value
          ? {
              from: formatDateForTracking(searchStartDate.value),
              to: formatDateForTracking(searchEndDate.value),
            }
          : undefined,
    },
  });
};

onMounted(() =>
  trackSurveyPageView(SURVEY_SEARCH_PAGE_NAME, getCurrentPathForTracking()),
);
</script>

<template>
  <div class="surveys-container">
    <div class="header-title">
      <div class="header-content">
        <div class="c-title-bold--l">スレッド</div>
      </div>
    </div>
    <div class="header-tabs">
      <div class="header-content tabs">
        <TextTabItem text="作成履歴" is-selected />
      </div>
    </div>
    <div class="surveys-content">
      <div class="search-result-count">
        <div class="c-title-bold--m">スレッド</div>
        <div class="count-text c-text-regular--s">
          ・{{ searchResultTotalCount }}件
        </div>
      </div>
      <div class="search-condition">
        <SearchBar placeholder="スレッドを検索" v-model="searchKeyword" />
        <div class="search-condition-select-box">
          <DropdownDateRangeCalendar
            :range="{ from: searchStartDate, to: searchEndDate }"
            :preset="searchPresetActivity"
            :min="FIRST_PUBLISHED_AT"
            :max="getToday()"
            :is-open="true"
            :disabled="false"
            :font-size="'small'"
            :icon-size="'small'"
            @apply-date="applyDate"
          />
        </div>
      </div>
      <div class="search-result">
        <SwrScrollingPagination
          :key="searchConditionKey"
          :page-limit="SURVEY_SEARCH_PAGE_LIMIT"
          :pagination-func="paginationFunc"
          :data-accessor="dataAccessor"
          @fetch="fetchSurvey"
        >
          <template v-slot="{ items }">
            <div class="item" v-for="survey in items" :key="survey.session_id">
              <router-link
                :to="{
                  name: 'surveySession',
                  params: { survey_session_id: survey.session_id },
                }"
              >
                <SurveyCard
                  :key="survey.session_id"
                  :highlight-keywords="[searchKeywordForApi]"
                  :title="survey.title"
                  :content="survey.content || ''"
                  :user-name="userInfo.data?.value?.user_name || ''"
                  :user-icon-url="userInfo.data?.value?.image_url || ''"
                  :updated-at="formatDate(survey.updated_at)"
                />
              </router-link>
              <div class="spacing-08" />
            </div>
          </template>
        </SwrScrollingPagination>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.header-title {
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid $color-gray400;
  background-color: $color-white;
}
.header-tabs {
  display: flex;
  justify-content: center;
  background-color: $color-white;
}
.header-content {
  max-width: var(--survey-session-width);
  width: 100%;
}
.tabs {
  display: flex;
}
.surveys-content {
  max-width: var(--survey-session-width);
  margin: 0 auto;
  padding: 0 32px;
}
.search-result-count {
  display: flex;
  align-items: center;
  padding: 24px 0;
  gap: 4px;
  .count-text {
    color: $color-gray800;
  }
}
.search-condition {
  display: flex;
  flex-direction: column;
  gap: 16px;
  .search-condition-select-box {
    display: flex;
    gap: 16px;
  }
}
.search-result {
  padding: 16px 0;
  .item {
    cursor: pointer;
  }
}
</style>
